import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/apiServices";

// Async Thunks
export const fetchPayments = createAsyncThunk(
  "payments/fetchPayments",
  async () => {
    const response = await apiService.get("payments");
    return response.data;
  }
);

export const fetchPaymentsPaginated = createAsyncThunk(
  "payments/fetchPaymentsPaginated",
  async (params) => {
    const response = await apiService.get("payments/payments", { params });
    return response.data;
  }
);

export const fetchPaymentById = createAsyncThunk(
  "payments/fetchPaymentById",
  async (paymentId) => {
    const response = await apiService.get(`payments/${paymentId}`);
    return response.data;
  }
);

export const createPayment = createAsyncThunk(
  "payments/createPayment",
  async (paymentData) => {
    const response = await apiService.post("payments", paymentData);
    return response.data;
  }
);

export const updatePayment = createAsyncThunk(
  "payments/updatePayment",
  async ({ paymentId, paymentData }) => {
    const response = await apiService.put(`payments/${paymentId}`, paymentData);
    return response.data;
  }
);

export const deletePayment = createAsyncThunk(
  "payments/deletePayment",
  async (paymentId) => {
    await apiService.delete(`payments/${paymentId}`);
    return paymentId;
  }
);

export const fetchPaymentsByPurchaseId = createAsyncThunk(
  "payments/fetchByPurchaseId",
  async (purchaseId, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`payments/purchased/${purchaseId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Initial State
const initialState = {
  payments: [],
  purchasedPayments: [],
  payment: null,
  loading: false,
  error: null,
  pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1
  }
};

// Slice
const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    resetPaymentState: (state) => {
      state.payment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentsByPurchaseId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByPurchaseId.fulfilled, (state, action) => {
        state.loading = false;
        state.purchasedPayments = action.payload;
      })
      .addCase(fetchPaymentsByPurchaseId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPaymentsPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsPaginated.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.payments;
        state.pagination = {
          totalRecords: action.payload.totalRecords,
          totalPages: action.payload.totalPages,
          currentPage: action.payload.currentPage
        };
      })
      .addCase(fetchPaymentsPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchPaymentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.loading = false;
        state.payment = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(createPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments.push(action.payload);
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(updatePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.payments.findIndex(
          (payment) => payment._id === action.payload._id
        );
        if (index !== -1) {
          state.payments[index] = action.payload;
        }
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(deletePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = state.payments.filter(
          (payment) => payment._id !== action.payload
        );
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

// Actions
export const { resetPaymentState } = paymentSlice.actions;

// Reducer
export default paymentSlice.reducer;
